import { useState, useEffect, useCallback } from 'react'

export interface ImagePopupModalProps {
  isOpen: boolean
  imageSrc: string
  alt?: string
  onClose: () => void
}

const ZOOM_MIN = 0.5
const ZOOM_MAX = 3
const ZOOM_STEP = 0.25

export default function ImagePopupModal({ isOpen, imageSrc, alt = 'Image', onClose }: ImagePopupModalProps) {
  const [zoom, setZoom] = useState(1)
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const [drag, setDrag] = useState<{ startX: number; startY: number; startPanX: number; startPanY: number } | null>(null)

  const resetState = useCallback(() => {
    setZoom(1)
    setPan({ x: 0, y: 0 })
    setDrag(null)
  }, [])

  useEffect(() => {
    if (!isOpen) {
      resetState()
      return
    }
    const onMouseMove = (e: MouseEvent) => {
      if (!drag) return
      e.preventDefault()
      setPan({
        x: drag.startPanX + (e.clientX - drag.startX),
        y: drag.startPanY + (e.clientY - drag.startY)
      })
    }
    const onMouseUp = () => setDrag(null)
    const onWheel = (e: WheelEvent) => e.preventDefault()
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
    window.addEventListener('wheel', onWheel, { passive: false })
    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
      window.removeEventListener('wheel', onWheel)
    }
  }, [isOpen, drag])

  useEffect(() => {
    if (isOpen) resetState()
  }, [isOpen, imageSrc, resetState])

  const handleZoomIn = () => setZoom(z => Math.min(ZOOM_MAX, z + ZOOM_STEP))
  const handleZoomOut = () => setZoom(z => Math.max(ZOOM_MIN, z - ZOOM_STEP))

  if (!isOpen) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Image preview"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        backgroundColor: 'rgba(0, 0, 0, 0.65)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        boxSizing: 'border-box'
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          boxShadow: '0 24px 80px rgba(0,0,0,0.3)',
          width: '100%',
          maxWidth: 'min(920px, calc(100vw - 32px))',
          height: 'min(85vh, calc(100vh - 32px))',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header: zoom controls + close */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            borderBottom: '1px solid #E9E9EA',
            flexShrink: 0,
            gap: '12px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              type="button"
              onClick={handleZoomOut}
              disabled={zoom <= ZOOM_MIN}
              title="Zoom out"
              style={{
                padding: '8px',
                border: '1px solid #E9E9EA',
                background: '#fff',
                borderRadius: '8px',
                cursor: zoom <= ZOOM_MIN ? 'not-allowed' : 'pointer',
                opacity: zoom <= ZOOM_MIN ? 0.5 : 1
              }}
            >
              <span style={{ fontSize: '18px', lineHeight: 1 }}>−</span>
            </button>
            <span style={{ fontFamily: 'Lexend Deca', fontSize: '14px', color: '#212126', minWidth: '48px', textAlign: 'center' }}>
              {Math.round(zoom * 100)}%
            </span>
            <button
              type="button"
              onClick={handleZoomIn}
              disabled={zoom >= ZOOM_MAX}
              title="Zoom in"
              style={{
                padding: '8px',
                border: '1px solid #E9E9EA',
                background: '#fff',
                borderRadius: '8px',
                cursor: zoom >= ZOOM_MAX ? 'not-allowed' : 'pointer',
                opacity: zoom >= ZOOM_MAX ? 0.5 : 1
              }}
            >
              <span style={{ fontSize: '18px', lineHeight: 1 }}>+</span>
            </button>
          </div>
          <button
            type="button"
            onClick={onClose}
            title="Close"
            style={{
              padding: '8px',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              borderRadius: '8px',
              fontSize: '20px',
              lineHeight: 1,
              color: '#212126'
            }}
          >
            ×
          </button>
        </div>

        {/* Content: no scrollbars, image fits inside with object-fit contain */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: drag ? 'grabbing' : 'grab',
            userSelect: 'none',
            transition: drag ? 'none' : 'transform 0.15s ease-out'
          }}
          onMouseDown={e => {
            if (e.button === 0) {
              setDrag({
                startX: e.clientX,
                startY: e.clientY,
                startPanX: pan.x,
                startPanY: pan.y
              })
            }
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <div
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                transformOrigin: 'center center',
                maxWidth: '100%',
                maxHeight: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none'
              }}
            >
              <img
                src={imageSrc}
                alt={alt}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  verticalAlign: 'middle'
                }}
                draggable={false}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
